import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Spinner,
  Card,
  CardBody,
  CardFooter,
  Button,
  Grid,
  GridItem,
  Container,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  IconButton,
  Icon,
  Flex,
} from "@chakra-ui/react";
import { collection, query, where, onSnapshot, getDoc, doc, orderBy } from "firebase/firestore";
import { firestore, auth } from "../../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { IoMdChatbubbles, IoMdText } from "react-icons/io"; // Import chat icon
import { useTranslation } from "react-i18next";


const ChatListPage = () => {
  const { t, i18n } = useTranslation();

  const [buyerChats, setBuyerChats] = useState([]);
  const [sellerChats, setSellerChats] = useState([]);
  const [userName, setUserName] = useState("User");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const cardBgColor = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("brand.500", "white");
  const headingColor = useColorModeValue("brand.900", "white");
  const tabBgColor = useColorModeValue("brand.200", "gray.800");

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        console.warn(t("chatlistpage.auth_not_authenticated"));
        return;
      }

      try {
        const userDocRef = doc(firestore, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          setUserName(userData.userName || t("chatlistpage.unknown_user"));
        } else {
          console.warn(t("chatlistpage.user_doc_not_found"));
        }
      } catch (error) {
        console.error(t("chatlistpage.fetch_user_error"), error);
      }
    };

    const fetchChats = async () => {
      const user = auth.currentUser;
      if (!user) {
        console.warn(t("chatlistpage.auth_not_authenticated"));
        return;
      }

      // Order chats by lastMessageAt in descending order (latest chats first)
      const buyerQuery = query(
        collection(firestore, "chats"),
        where("buyerId", "==", user.uid),
        orderBy("lastMessageAt", "desc")
      );

      const sellerQuery = query(
        collection(firestore, "chats"),
        where("sellerId", "==", user.uid),
        orderBy("lastMessageAt", "desc")
      );

      const fetchChatDetails = async (chats) => {
        try {
          return await Promise.all(
            chats.map(async (chat) => {
              const listingDoc = await getDoc(doc(firestore, "listings", chat.listingId));
              const listingData = listingDoc.exists() ? listingDoc.data() : { title: t("chatlistpage.listing_unavailable") };

              return { ...chat, listing: listingData };
            })
          );
        } catch (error) {
          console.error(t("chatlistpage.fetch_chat_details_error"), error);
          return [];
        }
      };

      const unsubscribeBuyer = onSnapshot(buyerQuery, async (snapshot) => {
        const buyerChatsData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const updatedBuyerChats = await fetchChatDetails(buyerChatsData);
        setBuyerChats(updatedBuyerChats);
      });

      const unsubscribeSeller = onSnapshot(sellerQuery, async (snapshot) => {
        const sellerChatsData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const updatedSellerChats = await fetchChatDetails(sellerChatsData);
        setSellerChats(updatedSellerChats);
        setLoading(false);
      });

      return () => {
        unsubscribeBuyer();
        unsubscribeSeller();
      };
    };

    fetchUserData();
    fetchChats();
  }, []);

  if (loading) {
    return (
      <Box p={4} textAlign="center">
        <Spinner size="lg" />
        <Text color={textColor}>{t("chatlistpage.loading_chats")}</Text>
      </Box>
    );
  }

  const renderChatCard = (chat, isBuyer) => (
    <Card key={chat.id} shadow="md" borderRadius="lg" bg={cardBgColor} p={4} h="100%">
      <CardBody>
        <Grid templateColumns={{ base: "1fr", md: "3fr 1fr" }} gap={4} alignItems="center">
          {/* Chat Details */}
          <Box>
            <Text fontWeight="bold" fontSize="lg" color={textColor}>
              {isBuyer ? "Seller" : "Buyer"}: {userName || "Unknown"}
            </Text>
            <Text color={textColor}>{t("chatlistpage.amount_label")}: {chat.listing.amount || t("chatlistpage.not_available")} {chat.listing.offeredCurrency}</Text>
            <Text color={textColor}>{t("chatlistpage.rate_label")}: {chat.listing.rate || t("chatlistpage.not_available")} {chat.listing.exchangeRatePair}</Text>
            <Text color={textColor}>{t("chatlistpage.location_label")}: {chat.listing.location.town || t("chatlistpage.not_available")}</Text>
            <Text color={textColor}>{t("chatlistpage.status_label")}: {chat.listing.status || t("chatlistpage.not_available")}</Text>
          </Box>
  
          {/* Chat Icon Button */}
          <Flex justify={{ base: "center", md: "flex-end" }} align="center">
            <IconButton
              aria-label={t("chatlistpage.open_chat")}
              icon={<Icon as={IoMdText} boxSize={{ base: 12, md: 16 }} />} // Responsive icon size
              colorScheme="blue"
              variant="ghost"
              color="brand.300"
              onClick={() => navigate(`/personal/chat/${chat.id}`)}
              _hover={{ bg: "brand.100", color:"brand.300", transform: "scale(1.1)" }}
              />
          </Flex>
        </Grid>
      </CardBody>
    </Card>
  );
  

  return (
    <Box bg="brand.200" color="brand.500">
      {/* Hero Section */}
      <Box
        py={12}
        px={8}
        textAlign="center"
        // bgGradient="linear(to-r, blue.500, blue.400)"
        color="brand.300"
        borderRadius="lg"
      >
        <Container maxW="container.md">
          <Heading 
            as={motion.h1}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            size="2xl"
            fontWeight="bold"
            mb={4}
            color="brand.300"
          >
            {t("chatlistpage.chat_list_title")}
            </Heading>
          <Text 
            as={motion.p}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            fontSize="xl" 
            mb={6}
            color="brand.400"
          >
          {t("chatlistpage.chat_list_description")}
          </Text>
        </Container>
      </Box>

      {/* Chat Tabs Section */}
      <Tabs variant="soft-rounded" colorScheme="blue" mt={6}>
        <TabList justifyContent="center" bg={tabBgColor} p={3} borderRadius="md">
        <Tab fontSize="lg">{t("chatlistpage.buyer_chats_tab")}</Tab>
        <Tab fontSize="lg">{t("chatlistpage.seller_chats_tab")}</Tab>
        </TabList>
        <TabPanels>
          {/* Buyer Chats */}
          <TabPanel>
            {buyerChats.length === 0 ? (
              <Text textAlign="center" color={textColor}>
                {t("chatlistpage.no_buyer_chats")}
                </Text>
            ) : (
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
                {buyerChats.map((chat) => (
                  <GridItem key={chat.id}>{renderChatCard(chat, true)}</GridItem>
                ))}
              </Grid>
            )}
          </TabPanel>

          {/* Seller Chats */}
          <TabPanel>
            {sellerChats.length === 0 ? (
              <Text textAlign="center" color={textColor}>
                {t("chatlistpage.no_seller_chats")}
                </Text>
            ) : (
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
                {sellerChats.map((chat) => (
                  <GridItem key={chat.id}>{renderChatCard(chat, false)}</GridItem>
                ))}
              </Grid>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default ChatListPage;
